import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getDocumentUploadUrl, uploadDocumentToS3, processDocument, getWorkspaceItems } from '../../services/api';

const ACCEPTED_TYPES = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};

const STEPS = [
  { key: 'select', label: 'Select File' },
  { key: 'uploading', label: 'Uploading' },
  { key: 'extracting', label: 'Extracting' },
  { key: 'complete', label: 'Complete' },
];

function DocumentUpload({ onNavigate }) {
  const [step, setStep] = useState('select');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [workspaceId, setWorkspaceId] = useState(null);
  const fileInputRef = useRef(null);

  const validateFile = useCallback((f) => {
    if (!f) return 'No file selected';
    if (!ACCEPTED_TYPES[f.type]) {
      return 'Invalid file type. Only PDF and DOCX files are accepted.';
    }
    if (f.size > 50 * 1024 * 1024) {
      return 'File too large. Maximum size is 50MB.';
    }
    return null;
  }, []);

  const handleFile = useCallback((f) => {
    const err = validateFile(f);
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setFile(f);
  }, [validateFile]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleInputChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  }, [handleFile]);

  // Poll for extraction completion when in 'extracting' step
  useEffect(() => {
    if (step !== 'extracting' || !workspaceId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 60; // poll for up to ~2 minutes

    async function poll() {
      while (!cancelled && attempts < maxAttempts) {
        attempts++;
        try {
          const items = await getWorkspaceItems();
          const item = items.find((i) => i.workspaceId === workspaceId);
          if (item && item.status !== 'PENDING_EXTRACTION') {
            if (!cancelled) setStep('complete');
            return;
          }
        } catch {
          // ignore polling errors, keep trying
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) {
        // Timed out but extraction might still be running — go to complete anyway
        setStep('complete');
      }
    }

    poll();
    return () => { cancelled = true; };
  }, [step, workspaceId]);

  async function handleUpload() {
    if (!file) return;
    setError('');

    try {
      // Step 1: Get presigned URL
      setStep('uploading');
      const { uploadUrl, workspaceId: wsId } = await getDocumentUploadUrl(
        file.name,
        file.type
      );

      // Step 2: Upload to S3
      const success = await uploadDocumentToS3(uploadUrl, file);
      if (!success) {
        throw new Error('Failed to upload file to S3');
      }

      // Step 3: Trigger extraction (fire-and-forget — Lambda may take longer than API Gateway timeout)
      setStep('extracting');
      setWorkspaceId(wsId);
      processDocument(wsId).catch(() => {
        // API Gateway may time out for scanned PDFs, but Lambda keeps running.
        // The polling useEffect above will detect completion.
      });
    } catch (err) {
      setError(err.message || 'Upload failed');
      setStep('select');
    }
  }

  function handleGoToWorkspace() {
    if (workspaceId) {
      onNavigate('workspace-detail', { workspaceId });
    } else {
      onNavigate('workspace');
    }
  }

  const currentStepIdx = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="form-page">
      <button className="form-back-link" onClick={() => onNavigate('workspace')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to Workspace
      </button>

      <div className="form-card">
        {/* Progress Steps */}
        <div className="upload-steps">
          {STEPS.map((s, idx) => (
            <div
              key={s.key}
              className={`upload-step${idx <= currentStepIdx ? ' active' : ''}${idx < currentStepIdx ? ' completed' : ''}`}
            >
              <div className="upload-step-dot">
                {idx < currentStepIdx ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span>{idx + 1}</span>
                )}
              </div>
              <span className="upload-step-label">{s.label}</span>
              {idx < STEPS.length - 1 && <div className="upload-step-line" />}
            </div>
          ))}
        </div>

        {/* Select File State */}
        {step === 'select' && (
          <>
            <div
              className={`upload-dropzone${dragActive ? ' drag-active' : ''}${file ? ' has-file' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                onChange={handleInputChange}
                style={{ display: 'none' }}
              />
              {file ? (
                <div className="upload-file-info">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <div className="upload-file-name">{file.name}</div>
                  <div className="upload-file-size">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                  <button
                    className="upload-file-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="upload-dropzone-content">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <div className="upload-dropzone-text">
                    Drag and drop your contract document here
                  </div>
                  <div className="upload-dropzone-hint">
                    or click to browse &bull; PDF and DOCX only &bull; Max 50MB
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="form-api-error">{error}</div>
            )}

            <div className="form-actions">
              <button
                className="btn btn-outline"
                onClick={() => onNavigate('workspace')}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={!file}
              >
                Upload & Process
              </button>
            </div>
          </>
        )}

        {/* Uploading State */}
        {step === 'uploading' && (
          <div className="upload-processing">
            <div className="upload-spinner" />
            <div className="upload-processing-title">Uploading Document</div>
            <div className="upload-processing-sub">
              Transferring {file?.name} to secure storage...
            </div>
          </div>
        )}

        {/* Extracting State */}
        {step === 'extracting' && (
          <div className="upload-processing">
            <div className="upload-spinner" />
            <div className="upload-processing-title">Extracting Contract Data</div>
            <div className="upload-processing-sub">
              AI is analyzing the document and extracting key fields...
            </div>
            <div className="upload-processing-sub" style={{ marginTop: '8px', fontSize: '11px', opacity: 0.5 }}>
              Scanned documents may take up to 60 seconds for OCR processing.
            </div>
          </div>
        )}

        {/* Complete State */}
        {step === 'complete' && (
          <div className="upload-processing">
            <div className="upload-complete-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="upload-processing-title">Extraction Complete</div>
            <div className="upload-processing-sub">
              Contract data has been extracted. Review and complete any missing fields.
            </div>
            <div className="form-actions" style={{ justifyContent: 'center' }}>
              <button className="btn btn-primary" onClick={handleGoToWorkspace}>
                Review Extracted Data
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

DocumentUpload.propTypes = {
  onNavigate: PropTypes.func.isRequired,
};

export default DocumentUpload;
